// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import type { ProviderFormSubmissionRequest, ProviderSubmissionResult } from '../types/providerAccess';
import type { WizardSubmissionResult, FileUploadResult, PendingUpload } from '../types/wizard';

/**
 * API functions injected by the consuming app (advisor-ui or provider-ui).
 */
export interface WizardSubmissionApi {
  submitForm: (token: string, formData: ProviderFormSubmissionRequest) => Promise<ProviderSubmissionResult>;
  uploadDocument: (
    token: string, file: File, stepId: string, fieldKey: string,
    onProgress?: (progress: number) => void,
  ) => Promise<ProviderSubmissionResult>;
  extractError: (err: unknown) => { error?: string };
}

interface UseWizardSubmissionResult {
  submitting: boolean;
  progressText: string;
  uploadProgress: number | null;
  result: WizardSubmissionResult | null;
  error: string | null;
  submitWizard: (
    token: string, formData: ProviderFormSubmissionRequest,
    pendingUploads: PendingUpload[], filesByKey: Map<string, File>,
  ) => Promise<WizardSubmissionResult>;
  reset: () => void;
}

export function useWizardSubmission(api: WizardSubmissionApi): UseWizardSubmissionResult {
  const [submitting, setSubmitting] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [result, setResult] = useState<WizardSubmissionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitWizard = useCallback(
    async (
      token: string, formData: ProviderFormSubmissionRequest,
      pendingUploads: PendingUpload[], filesByKey: Map<string, File>,
    ): Promise<WizardSubmissionResult> => {
      setSubmitting(true);
      setError(null);
      setResult(null);
      setProgressText('Submitting form data...');
      setUploadProgress(null);

      let formSubmissionResult = null;
      try {
        formSubmissionResult = await api.submitForm(token, formData);
      } catch (err) {
        const parsedError = api.extractError(err);
        setError(parsedError.error || 'Submission failed');
        setSubmitting(false);
        setProgressText('');
        const failedResult: WizardSubmissionResult = {
          formSubmissionResult: null, fileUploadResults: [],
          allUploadsSucceeded: false, formSubmissionSucceeded: false,
        };
        setResult(failedResult);
        return failedResult;
      }

      const fileUploadResults: FileUploadResult[] = [];
      for (let i = 0; i < pendingUploads.length; i++) {
        const upload = pendingUploads[i];
        const fileKey = `${upload.stepId}:${upload.fieldKey}`;
        const file = filesByKey.get(fileKey);
        setProgressText(`Uploading file ${i + 1} of ${pendingUploads.length}...`);
        setUploadProgress(0);

        if (!file) {
          fileUploadResults.push({
            fileName: upload.fileName, stepId: upload.stepId, fieldKey: upload.fieldKey,
            success: false, error: 'File no longer available. Please re-select the file.',
          });
          continue;
        }

        try {
          const uploadResult = await api.uploadDocument(token, file, upload.stepId, upload.fieldKey, setUploadProgress);
          fileUploadResults.push({
            fileName: upload.fileName, stepId: upload.stepId, fieldKey: upload.fieldKey,
            success: true, submissionId: uploadResult.submissionId,
          });
        } catch (err) {
          const parsedError = api.extractError(err);
          fileUploadResults.push({
            fileName: upload.fileName, stepId: upload.stepId, fieldKey: upload.fieldKey,
            success: false, error: parsedError.error || 'Upload failed',
          });
        }
      }

      const wizardResult: WizardSubmissionResult = {
        formSubmissionResult, fileUploadResults,
        allUploadsSucceeded: fileUploadResults.length === 0 || fileUploadResults.every((r) => r.success),
        formSubmissionSucceeded: true,
      };
      setResult(wizardResult);
      setSubmitting(false);
      setProgressText('');
      setUploadProgress(null);
      return wizardResult;
    },
    [api],
  );

  const reset = useCallback(() => {
    setSubmitting(false);
    setProgressText('');
    setUploadProgress(null);
    setResult(null);
    setError(null);
  }, []);

  return { submitting, progressText, uploadProgress, result, error, submitWizard, reset };
}
